import { type PayloadAction, createSlice } from '@reduxjs/toolkit'
import debounce from 'lodash/debounce'
import type { AppStore } from '.'

const initialState = {
  query: '',
  locationType: '',
  location: undefined,
  displayMap: false,
  preFilterLocationType: undefined
}

type FilterState = {
  query: string
  locationType: string
  location?: {
    lat: number
    lng: number
  }
  displayMap: boolean
  preFilterLocationType?: string
}

function stateFromQueryString(): Partial<FilterState> {
  const params = new URLSearchParams(window.location.search)
  const lat = Number.parseFloat(params.get('lat') || '0')
  const lng = Number.parseFloat(params.get('lng') || '0')
  return {
    location: lat && lng ? { lat, lng } : undefined,
    displayMap: params.get('map') === '1'
  }
}

const updateQueryString = debounce(function updateQueryString(
  state: FilterState
) {
  const lastState = stateFromQueryString()
  if (
    state.location?.lat === lastState.location?.lat &&
    state.location?.lng === lastState.location?.lng &&
    state.displayMap === lastState.displayMap
  ) {
    return
  }

  const params = new URLSearchParams(window.location.search)

  if (!!state.location?.lat && !!state.location?.lng) {
    params.set('lat', state.location?.lat.toString())
    params.set('lng', state.location?.lng.toString())
  } else {
    params.delete('lat')
    params.delete('lng')
  }
  if (state.displayMap) {
    params.set('map', '1')
  } else {
    params.delete('map')
  }

  const newUrl = `${window.location.pathname}?${params.toString()}`
  window.history.pushState({}, '', newUrl)
}, 400)

export function connectFilterSliceToQueryString(store: AppStore) {
  store.subscribe(() => {
    const state = store.getState().filter
    updateQueryString(state)
  })
}

export const filterSlice = createSlice({
  name: 'filter',
  initialState: {
    ...initialState,
    ...stateFromQueryString()
  },
  reducers: {
    setQuery(state, action: PayloadAction<string>) {
      state.query = action.payload
    },
    setLocationType(state, action: PayloadAction<string>) {
      state.locationType = action.payload
    },
    setLocation(state, action: PayloadAction<{ lat: number; lng: number }>) {
      state.location = action.payload
    },
    setDisplayMap(state, action: PayloadAction<boolean>) {
      state.displayMap = action.payload
    },
    setPreFilterLocationType(state, action: PayloadAction<string | undefined>) {
      state.preFilterLocationType = action.payload
    }
  }
})

export const filterActions = filterSlice.actions
